import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import type { EventWithItems, PublicParticipant, Claim } from '@trueup/shared';
import { calculateParticipantTotal } from '@trueup/shared';
import { MultiQuantityItemCard, SingleItemCard, ReceiptItemList } from '@/components/ItemCard';
import ClaimSummary from '@/components/ClaimSummary';
import Logo from '@/components/Logo';
import { formatPrice, formatDate, receiptFont } from '@/lib/formatting';

export default function EventPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventWithItems | null>(null);
  const [participant, setParticipant] = useState<PublicParticipant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const updateItemClaim = useCallback(
    (itemId: string, claim: Claim, claimParticipant: PublicParticipant) => {
      setEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            const existingClaimIndex = item.claims.findIndex(
              (c) => c.participantId === claim.participantId
            );
            if (existingClaimIndex >= 0) {
              const newClaims = [...item.claims];
              newClaims[existingClaimIndex] = { ...claim, participant: claimParticipant };
              return { ...item, claims: newClaims };
            }
            return {
              ...item,
              claims: [...item.claims, { ...claim, participant: claimParticipant }],
            };
          }),
        };
      });
    },
    []
  );

  const removeItemClaim = useCallback((itemId: string, participantId: string) => {
    setEvent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => {
          if (item.id !== itemId) return item;
          return {
            ...item,
            claims: item.claims.filter((c) => c.participantId !== participantId),
          };
        }),
      };
    });
  }, []);

  const updateUnitClaim = useCallback(
    (itemId: string, unitNumber: number, claim: Claim, claimParticipant: PublicParticipant) => {
      setEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            // Remove existing claim for this unit
            const filteredClaims = item.claims.filter((c) => c.unitNumber !== unitNumber);
            return {
              ...item,
              claims: [...filteredClaims, { ...claim, participant: claimParticipant }],
            };
          }),
        };
      });
    },
    []
  );

  const removeUnitClaim = useCallback((itemId: string, unitNumber: number, participantId: string) => {
    setEvent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => {
          if (item.id !== itemId) return item;
          return {
            ...item,
            claims: item.claims.filter(
              (c) => !(c.unitNumber === unitNumber && c.participantId === participantId)
            ),
          };
        }),
      };
    });
  }, []);

  const updateFractionClaim = useCallback(
    (itemId: string, claim: Claim, claimParticipant: PublicParticipant) => {
      setEvent((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => {
            if (item.id !== itemId) return item;
            // Remove existing fractional claim from this participant
            const filteredClaims = item.claims.filter(
              (c) => !(c.participantId === claim.participantId && c.unitNumber == null)
            );
            return {
              ...item,
              claims: [...filteredClaims, { ...claim, participant: claimParticipant }],
            };
          }),
        };
      });
    },
    []
  );

  const removeFractionClaim = useCallback((itemId: string, participantId: string) => {
    setEvent((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => {
          if (item.id !== itemId) return item;
          return {
            ...item,
            claims: item.claims.filter(
              (c) => !(c.participantId === participantId && c.unitNumber == null)
            ),
          };
        }),
      };
    });
  }, []);

  const addParticipant = useCallback((newParticipant: PublicParticipant) => {
    setEvent((prev) => {
      if (!prev) return prev;
      if (prev.participants.some((p) => p.id === newParticipant.id)) return prev;
      return {
        ...prev,
        participants: [...prev.participants, newParticipant],
      };
    });
  }, []);

  // Handle scroll to show/hide shadow - must be before any early returns
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setHasScrolled(scrollTop > 10);
  }, []);

  const { connect, join, claimUnit, unclaimUnit, claimFraction, unclaimFraction, markActivity, isConnected, isReconnecting } = useWebSocket({
    onJoined: (p, e) => {
      setParticipant(p);
      setEvent(e);
      setLoading(false);
    },
    onItemClaimed: updateItemClaim,
    onItemUnclaimed: removeItemClaim,
    onUnitClaimed: updateUnitClaim,
    onUnitUnclaimed: removeUnitClaim,
    onFractionClaimed: updateFractionClaim,
    onFractionUnclaimed: removeFractionClaim,
    onParticipantJoined: addParticipant,
    onEventUpdated: setEvent,
    onError: setError,
  });

  useEffect(() => {
    if (!code) return;

    const storedParticipant = api.getStoredParticipant(code);
    if (!storedParticipant) {
      navigate(`/${code}`, { replace: true });
      return;
    }

    setParticipant(storedParticipant);
    connect();
    // Join message will be queued and sent when connection opens
    // Token is stored separately in localStorage, not in the participant object
    const sessionToken = localStorage.getItem('sessionToken') ?? undefined;
    join(code, storedParticipant.name, sessionToken);
  }, [code, navigate, connect, join]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mb-4">
            <Logo fontSize={40} />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Connecting...</p>
        </div>
      </div>
    );
  }

  if (error || !event || !participant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="mb-4">
            <Logo fontSize={40} />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{error || 'Failed to load event'}</p>
          <button
            onClick={() => {
              if (code) api.clearSession(code);
              navigate(`/${code}`);
            }}
            className="btn btn-primary"
          >
            Rejoin Event
          </button>
        </div>
      </div>
    );
  }

  // Calculate total with fractional support
  const myClaimedAmounts: { amount: number; portionNumerator?: number; portionDenominator?: number }[] = [];
  for (const item of event.items) {
    const myClaims = item.claims.filter((c) => c.participantId === participant.id);
    for (const claim of myClaims) {
      // For multi-quantity items, use unit price
      const amount = item.quantity > 1 ? Math.round(item.amount / item.quantity) : item.amount;
      myClaimedAmounts.push({
        amount,
        portionNumerator: claim.portionNumerator ?? 1,
        portionDenominator: claim.portionDenominator ?? 1,
      });
    }
  }

  const myTotal = calculateParticipantTotal(
    myClaimedAmounts,
    event.subtotal || 0,
    event.total || 0
  );

  const isClaimingOpen = event.status === 'active';

  // Build receipt image URL if available
  const receiptImageUrl = event.receiptImageKey
    ? `${import.meta.env.VITE_API_URL || ''}/api/receipts/${event.receiptImageKey}`
    : null;

  // Get host name
  const hostName = event.participants.find(p => p.id === event.adminId)?.name || 'the host';

  // Show confirmation view
  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 pt-52">
        <div className="text-center max-w-md w-full">
          <Logo fontSize={48} showTagline={false} />

          {/* Payment message */}
          <div className="mt-8">
            {myTotal > 0 && (
              <p className="text-lg text-gray-700">
                Please pay {hostName} <span className="font-bold text-blue-600">{formatPrice(myTotal)}</span>.
              </p>
            )}
            <p className="text-gray-500 mt-4">You can now close this window.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden" onClick={markActivity}>
      {/* Header with Logo and event name - matching iOS toolbar */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 py-2">
        <div className="max-w-lg md:max-w-xl lg:max-w-2xl mx-auto flex items-start px-4">
          <div className="w-16" /> {/* Spacer for balance */}
          <div className="flex-1 flex flex-col items-center">
            <Logo fontSize={28} />
            <span className="text-xs text-gray-500 mt-1">{event.name || event.code}</span>
          </div>
          <div className="w-16 flex justify-end">
            <button
              onClick={() => setShowConfirmation(true)}
              className="bg-blue-500 text-white font-semibold text-sm px-4 py-1.5 rounded-full hover:bg-blue-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {/* Summary section - not sticky, just fixed at top of content area */}
      <div className="flex-shrink-0 z-20">
        <div
          className="max-w-lg md:max-w-xl lg:max-w-2xl mx-auto bg-white transition-shadow duration-200"
          style={{ boxShadow: hasScrolled ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none' }}
        >
          <ClaimSummary
            event={event}
            participant={participant}
            myTotal={myTotal}
            receiptImageUrl={receiptImageUrl}
          />
          {(isReconnecting || !isConnected) && (
            <div className="bg-yellow-50 px-4 py-2 text-center">
              <span className="text-yellow-700 text-sm">
                {isReconnecting ? 'Reconnecting...' : 'Connection lost. Tap anywhere to reconnect.'}
              </span>
            </div>
          )}
          {!isClaimingOpen && (
            <div className="bg-amber-50 px-4 py-2">
              <div className="flex items-center justify-center gap-2 text-amber-800">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-sm font-medium">Claiming is closed</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable receipt content - this is the only scrolling area */}
      <main className="flex-1 overflow-y-auto min-h-0" onScroll={handleScroll}>
        <div className="max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
          {/* Vendor name and date - matching iOS layout */}
          <div className="px-5 pt-5 pb-6" style={{ backgroundColor: 'rgb(252, 252, 250)' }}>
            {/* Vendor name - center aligned, iOS title3 = 20pt */}
            <div className="text-center text-gray-900 font-medium" style={{ ...receiptFont, fontSize: '20px' }}>
              {event.merchant || 'Vendor Not Clear'}
            </div>
            {/* Date - right aligned, iOS body = 17pt */}
            {event.receiptDate && (
              <div className="text-right text-gray-900 mt-6" style={{ ...receiptFont, fontSize: '17px' }}>
                {formatDate(event.receiptDate)}
              </div>
            )}
          </div>

          {/* Receipt-style item list */}
          <ReceiptItemList>
            {event.items.map((item) => {
              if (item.quantity > 1) {
                return (
                  <MultiQuantityItemCard
                    key={item.id}
                    item={item}
                    currentParticipantId={participant.id}
                    onClaimUnit={claimUnit}
                    onUnclaimUnit={unclaimUnit}
                    isClaimingOpen={isClaimingOpen}
                  />
                );
              } else {
                return (
                  <SingleItemCard
                    key={item.id}
                    item={item}
                    currentParticipantId={participant.id}
                    onClaimFraction={claimFraction}
                    onUnclaimFraction={unclaimFraction}
                    isClaimingOpen={isClaimingOpen}
                  />
                );
              }
            })}
          </ReceiptItemList>

          {/* Totals section */}
          <div className="bg-white px-5 py-4 border-t border-gray-200" style={receiptFont}>
            {event.subtotal != null && (
              <div className="flex justify-between text-gray-700 mb-2" style={{ fontSize: '17px' }}>
                <span>Subtotal</span>
                <span>{formatPrice(event.subtotal)}</span>
              </div>
            )}
            {event.tax != null && (
              <div className="flex justify-between text-gray-700 mb-2" style={{ fontSize: '17px' }}>
                <span>Tax</span>
                <span>{formatPrice(event.tax)}</span>
              </div>
            )}
            {event.total != null && (
              <div className="flex justify-between font-bold text-gray-900 pt-3 border-t border-gray-200" style={{ fontSize: '22px' }}>
                <span>TOTAL</span>
                <span>{formatPrice(event.total)}</span>
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
